import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import dayjs from 'dayjs';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import * as PropTypes from 'prop-types';

import JobContainer from 'components/jobs/container/JobContainer';
import LetterLinksNav from 'components/letterLinksNav';
import NoSSR from 'components/noSSR';
import QuestionAndAnswerSection from 'layouts/section/questionAndAnswerSection';
import JobTitleClusterFooter from 'widgets/clusterFooter/jobTitleClusterFooter/jobTitleClusterFooter';

import BottomClusterNavigation from '../components/bottomClusterNavigation';
import {
  CAROUSEL_FILTERS,
  CMP_FILTERS,
  FILTER_PARAMS,
  FILTER_TYPES,
  setupFilters,
  WORK_FROM_HOME,
} from '../components/zpFilter/filterUtils';
import DiversitySection from '../layouts/careerDemographicsPage/diversitySection';
import HeatmapsSection from '../layouts/careerDemographicsPage/heatmapsSection';
import Sidebar from '../layouts/careerDemographicsPage/sideBar';
import StatisticsSection from '../layouts/careerDemographicsPage/statisticsSection';
import { dispatchCreateJobBucket } from '../store/jobs/actions';
import { buildCmpSubpagesUrls, getLocations, getStateAbbr, isUserOutsideUS } from '../utils';
import { logEventGTM } from '../utils/analyticsGTM';
import { CLUSTER_HEADER_CATEGORY, CMP_SUBPAGES, JOB_BUCKET_GROUP } from '../utils/constant';
import BreadCrumbs from '../widgets/BreadCrumbs';
import ClusterHeader from '../widgets/clusterHeader';
import JobDismissBanner from '../widgets/dismiss/JobDismissBanner';
import Meta from '../widgets/Meta';

import classes from 'styles/pages/careerDemographicsPage.module.css';

const getGtmFilterEventData = (jobTitle, filterGroup) => {
  switch (filterGroup) {
    case `${JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_GENDER_ETHIC_LANGUAGE}-genders`:
      return {
        label: 'Gender Ratio Filter click',
        event: `User clicks job title drop-down filter in the "${jobTitle} Gender Ratio" section`,
      };
    case `${JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_GENDER_ETHIC_LANGUAGE}-ethnicities`:
      return {
        label: 'Ethnic Breakdown Filter click',
        event: `User clicks job title drop-down filter in the "${jobTitle} Ethnic Breakdown" section`,
      };
    case `${JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_GENDER_ETHIC_LANGUAGE}-languages`:
      return {
        label: 'Foreign Languages Spoken Filter Click',
        event: `User clicks job title drop-down filter in the "Foreign Languages Spoken By ${jobTitle}s" section`,
      };
    case JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_DURATION:
      return {
        label: 'Number Of Years On The Job Filter Click',
        event: `User clicks job title drop-down filter in the "Number Of Years On The Job" section`,
      };
    case JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_AGE_BREAKDOWN:
      return {
        label: 'Age Breakdown Filter Click',
        event: `User clicks job title drop-down filter in the "${jobTitle} Age Breakdown" section`,
      };
    case JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_GENDER_RATIO:
      return {
        label: 'Comparing The Gender Ratio Filter Click',
        event: `User clicks job title drop-down filter in the "Comparing The Gender Ratio of ${jobTitle}s with Other Job Titles" section`,
      };
    case JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_LGBT:
      return {
        label: 'Comparing The LGBT Ratio',
        event: `User clicks job title drop-down filter in the "Comparing The LGBT Ratio" section`,
      };
    case `${JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_JOBS}-companySizes`:
      return {
        label: 'Company Size Filter Click',
        event: `User clicks job title drop-down filter in the "Company Size" section`,
      };
    case `${JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_JOBS}-companyTypes`:
      return {
        label: 'The Types Of Companies Filter Click',
        event: `User clicks job title drop-down filter in the "The Types Of Companies ${jobTitle} Work In" section`,
      };
    case `${JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_JOBS}-industries`:
      return {
        label: 'Industry Concentration Filter Click',
        event: `User clicks job title drop-down filter in the "Industry Concentration" section`,
      };
  }
};

class CareerDemographicsPage extends React.Component {
  static async getInitialProps({ req, res }) {
    if (req) {
      return {
        data: res ? res.data : null,
        user: req ? req.user : null,
        locals: res ? res.locals : null,
      };
    }
    return window.__NEXT_DATA__.props.pageProps;
  }

  constructor(props) {
    super(props);

    const {
      data: { demographicsData },
    } = props;
    const { salaryMapExists, demandMapExists, supplyMapExists } = demographicsData || {};

    this.state = {
      showHeatmapsSection: !!(salaryMapExists || demandMapExists || supplyMapExists),
    };
  }

  componentDidMount() {
    const { dispatchCreateJobBucket } = this.props;
    dispatchCreateJobBucket({ key: JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_JOBS_BUCKET });
    dispatchCreateJobBucket({ key: JOB_BUCKET_GROUP.RECENT_JOB_TITLE_JOB_BUCKET });
    dispatchCreateJobBucket({ key: JOB_BUCKET_GROUP.PAID_JOBS });
  }

  hideHeatmapsSection = () => {
    this.setState({
      showHeatmapsSection: false,
    });
  };

  generateFiltersForJobBuckets = () => {
    const {
      user,
      data: { criteria = {} },
    } = this.props;

    const filters = [
      {
        ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
        type: FILTER_TYPES.AUTOSUGGEST,
        onInputChange: ({ originalSearch, normalizedTitle }) => {
          const title = normalizedTitle || originalSearch || '';
          if (title) window.location.href = buildCmpSubpagesUrls(title, '', 'jobs');
        },
      },
      ...CMP_FILTERS,
    ];

    const fields = filters.map((filter) => {
      switch (filter.param) {
        case FILTER_PARAMS.LOCATION:
          return {
            preselectedItems: [
              ...getLocations(user?.userLocation, criteria),
              ...(isUserOutsideUS(criteria)
                ? []
                : [{ displayName: WORK_FROM_HOME, value: WORK_FROM_HOME }]),
            ],
          };
        default:
          return {};
      }
    });

    return setupFilters(filters, fields);
  };

  logFilterButtonClickEvent = (filterGroup) => {
    const {
      criteria: { title },
    } = this.props.data;
    logEventGTM({
      action: 'Filter Click',
      ...getGtmFilterEventData(title, filterGroup),
    });
  };

  render() {
    const { data, user, jobBucket, jobListDismiss, recentTitleJobs } = this.props;
    const {
      metadata,
      breadCrumbs,
      showClusterHeader,
      criteria,
      genderRatio,
      demographicPieChartData,
      heatmapsData,
      articles,
      subheaderItems,
      demographicsData,
      desktopDevice,
      titleIndexes,
      footerLinksData,
      questionsAndAnswerData,
      inDemandJobs,
      nationalAverageAnnualSalary,
      jobTitlesByLetter,
    } = data;
    const currentURL = buildCmpSubpagesUrls(criteria?.title, "", CMP_SUBPAGES.DEMOGRAPHICS);

    const getMostCommonRace = () => {
      const { pieChartData } = demographicPieChartData;
      const degreeChartData = pieChartData.find((chart) => chart.key === 'ethnicities');
      if (degreeChartData) {
        return degreeChartData.chartData.slice(0, 2);
      }
      return null;
    };

    const getJobsFilters = () => {
      const filters = [
        {
          ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
          type: FILTER_TYPES.AUTOSUGGEST,
          onInputChange: ({ originalSearch, normalizedTitle }) => {
            const title = normalizedTitle || originalSearch || '';
            if (title) window.location.href = buildCmpSubpagesUrls(title, '', 'jobs');
          },
        },
        ...CMP_FILTERS,
      ];

      const fields = filters.map(({ param }) => {
        switch (param) {
          case FILTER_PARAMS.LOCATION:
            return {
              preselectedItems: [
                ...getLocations(user?.userLocation, criteria),
                ...(isUserOutsideUS(criteria)
                  ? []
                  : [{ displayName: WORK_FROM_HOME, value: WORK_FROM_HOME }]),
              ],
            };
          default:
            return {};
        }
      });

      return setupFilters(filters, fields);
    };

    const mostCommonRace = getMostCommonRace();

    const { UPDATED_TIME } = metadata;
    const lastUpdatedDate = dayjs(UPDATED_TIME).format('MMMM D, YYYY');

    const { showHeatmapsSection } = this.state;

    const statisticsContentBlurb = articles.find(
      (article) => article.post_subtype === 'statistics'
    );

    let locations = [];
    if (!_isEmpty(user) && user.userLocation && user.userLocation.length) {
      user.userLocation.forEach((location) => {
        const locationName = `${location.city ? location.city : ''}${
          location.state ? `${location.city ? ', ' : ''}${location.state}` : ''
        }`;
        locations.push({
          displayName: locationName,
          valueObj: { city: location.city, state: getStateAbbr(location.state) },
          value: locationName.toLowerCase(),
          city: location.city,
          state: getStateAbbr(location.state),
          enabled: true,
        });
      });
    } else if (criteria && criteria.geoCity && criteria.geoStateCode) {
      const locationName = `${criteria.geoCity ? criteria.geoCity : ''}${
        criteria.geoStateCode ? `${criteria.geoCity ? ', ' : ''}${criteria.geoStateCode}` : ''
      }`;
      locations.push({
        displayName: locationName,
        valueObj: { city: criteria.geoCity, state: getStateAbbr(criteria.geoStateCode) },
        value: locationName.toLowerCase(),
        city: criteria.geoCity,
        state: getStateAbbr(criteria.geoStateCode),
        enabled: true,
      });
    }

    const userLocations = getLocations(user?.userLocation, criteria);

    const sidebarProps = {
      jobTitle: criteria.title,
      averageAge: _get(demographicsData, 'ages.average', ''),
      genderRatio: genderRatio,
      genderOverTime: demographicsData.genderOverTime,
      jobsIndex: titleIndexes.indexInfoOfJobsPage === 'INDEX',
      locations: userLocations,
      origin: CMP_SUBPAGES.DEMOGRAPHICS,
      jobListDismiss: jobListDismiss,
      ethnicities: mostCommonRace,
      averageSalary: nationalAverageAnnualSalary,
      calloutSrc: '#src=demographics-callout-jobs1012-l10',
    };

    return (
      <div className={classes['career-demographics-page-container']}>
        <JobDismissBanner />

        <Meta {...metadata} />
        {showClusterHeader && (
          <ClusterHeader
            data={data}
            subPageName={CMP_SUBPAGES.DEMOGRAPHICS}
            clusterCategory={CLUSTER_HEADER_CATEGORY.CAREER}
            subPageTextSmall={false}
          />
        )}

        <div className="container">
          <div className="d-flex pt-0 pt-md-5">
            <main className="w-730 d-flex flex-column">
              <section id="intro-section">
                <div className="intro-section-container">
                  <div className="intro-section-wrapper d-flex flex-row flex-lg-column">
                    <Sidebar {...sidebarProps} className="d-block d-lg-none" />
                  </div>
                </div>
              </section>

              <div className='order-first'>
                {!_isEmpty(statisticsContentBlurb) && (
                  <section id="statistics-section">
                    <StatisticsSection statisticsContentBlurb={statisticsContentBlurb} />
                  </section>
                )}

                <div className={cn(
                  classes['career-demographics-page-in-page-nav'],
                  {"z-mt-48" : !_isEmpty(statisticsContentBlurb) || !desktopDevice,
                  "z-mb-md-48 z-mb-16" : _isEmpty(statisticsContentBlurb)}
                )}>
                  <div className='zp-title-h3 text-capitalize z-mb-16'>
                    On This Page
                  </div>
                  <div>
                    <span className='text-capitalize'>
                      {`${criteria.title} demographics by: `}
                    </span>
                    <span>
                      <ul>
                        <li className='d-inline-block'>
                          <a className='link font-16' href={`${currentURL}#gender-statistics`}>Gender</a>
                        </li>
                        <li className='d-inline-block'>
                          <a className='link font-16' href={`${currentURL}#race-statistics`}>Race</a>
                        </li>
                        <li className='d-inline-block'>
                          <a className='link font-16' href={`${currentURL}#age-statistics`}>Age</a>
                        </li>
                        <li className='d-inline-block'>
                          <a className='link font-16' href={`${currentURL}#degree-level-types`}>Education</a>
                        </li>
                        <li className='d-inline-block'>
                          <a className='link font-16' href={`${currentURL}#employment-statistics`}>Statistics</a>
                        </li>
                      </ul>
                    </span>
                  </div>
                </div>
              </div>
              {(!desktopDevice || (desktopDevice && !_isEmpty(statisticsContentBlurb))) && (
                <div className="section-separator-large" />
              )}

              <section id="diversity-section">
                <DiversitySection
                  filterLogEventCallback={this.logFilterButtonClickEvent}
                  criteria={criteria}
                  jobTitle={criteria.title}
                  demographicPieChartData={demographicPieChartData}
                  genderRatio={genderRatio}
                  ages={demographicsData.ages}
                  durations={demographicsData.durations}
                  genderOverTime={demographicsData.genderOverTime}
                  raceEthnicitiesOverTime={demographicsData.ethnicitiesOverTime}
                  employmentOverTime={demographicsData.employmentOverTime}
                  articles={articles}
                  lgbt={demographicsData.lgbt}
                  lgbtSkewTitles={demographicsData.lgbtSkewTitles}
                  salaryEstimates={demographicsData.salaryEstimates}
                  ageByEthnicityGender={demographicsData.ageByEthnicityGender}
                  jobsIndex={titleIndexes.indexInfoOfJobsPage === 'INDEX'}
                  salaryIndex={titleIndexes.indexInfoOfSalaryPage === 'INDEX'}
                  educationIndex={titleIndexes.indexInfoOfEducationPage === 'INDEX'}
                  locations={userLocations}
                  jobListDismiss={jobListDismiss}
                  jobContainerFilters={this.generateFiltersForJobBuckets()}
                  userLocation={getLocations(user?.userLocation)}
                  jobBucket={jobBucket}
                  isDesktop={desktopDevice}
                  demographicsData={demographicsData}
                  inDemandJobs={inDemandJobs}
                />
              </section>
            </main>
            <Sidebar {...sidebarProps} />
          </div>
          {!recentTitleJobs && (
            <NoSSR>
              <section data-title="RECENT JOBS" id="recent-jobs-section">
                <div className="section-separator-large" />
                <div data-nav-title="Recent Jobs" id="recent-jobs">
                  <JobContainer
                    layout="bucket"
                    title={`Recently Added ${criteria.title} Jobs`}
                    jobGroup={JOB_BUCKET_GROUP.RECENT_JOB_TITLE_JOB_BUCKET}
                    initialPayload={{
                      title: criteria.title,
                      postingDateRange: '2d',
                      pageNo: 1,
                      ...(!isUserOutsideUS(criteria) ? { remoteCategories: [WORK_FROM_HOME] } : {}),
                    }}
                    isDesktop={desktopDevice}
                    filters={getJobsFilters()}
                    filterGroup={JOB_BUCKET_GROUP.RECENT_JOB_TITLE_JOB_BUCKET}
                  />
                </div>
              </section>
            </NoSSR>
          )}
          {showHeatmapsSection && (
            <section id="heatmaps-section">
              <div className="section-separator-large" />
              <HeatmapsSection
                criteria={criteria}
                articleData={articles}
                heatmapsData={heatmapsData}
                onAllHeatmapsLoadError={this.hideHeatmapsSection}
                demographicsData={demographicsData}
              />
            </section>
          )}
          {!!questionsAndAnswerData?.answers?.length && (
            <div className="container p-0">
              <QuestionAndAnswerSection
                title={questionsAndAnswerData?.sectionTitle}
                data={questionsAndAnswerData}
                criteria={criteria}
                padding={false}
                src="#src=demographics-callout-jobs1014-l20"
              />
            </div>
          )}
          {!!footerLinksData.length && (
            <section className="career-job-footer-section">
              <div className="section-separator-large" />
              {/* <CmpFooterSection
                                universalFooter={relatedLinks}
                            /> */}
              <JobTitleClusterFooter linksData={footerLinksData} />
            </section>
          )}
          {!_isEmpty(subheaderItems) && (
            <div className="container px-0">
              <BottomClusterNavigation subheaderItems={subheaderItems} />
            </div>
          )}
          <BreadCrumbs
            breadCrumbs={breadCrumbs}
            listClassName="mb-0"
            lastUpdatedDate={lastUpdatedDate}
          />

          <div className="section-separator-large p-0 z-my-20" />

          <LetterLinksNav
            title={`Browse ${criteria.industry.toLowerCase()} jobs`}
            letterLinks={jobTitlesByLetter}
            containerClassName="z-mb-20"
          />
        </div>
      </div>
    );
  }
}

CareerDemographicsPage.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  user: PropTypes.objectOf(PropTypes.any),
  locals: PropTypes.objectOf(PropTypes.any),
  dispatchCreateJobBucket: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  dispatchCreateJobBucket,
};

const mapStateToProps = ({ searchFilter, jobs }) => {
  // false = show job container
  // true = hide job container
  const jobBucket = jobs[JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_JOBS_BUCKET];
  const recentTitleJobs = jobs[JOB_BUCKET_GROUP.RECENT_JOB_TITLE_JOB_BUCKET];
  return {
    filters: searchFilter.filters,
    jobBucket: jobBucket
      ? jobBucket.hideOnEmpty && jobBucket.totalJobs === 0 && jobBucket.loading === false
      : false,
    jobListDismiss: jobs.jobListDismiss,
    recentTitleJobs: recentTitleJobs
      ? recentTitleJobs.hideOnEmpty &&
        recentTitleJobs.totalJobs === 0 &&
        recentTitleJobs.loading === false
      : false,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CareerDemographicsPage);
